import * as React from 'react';
import { Popover } from 'radix-ui';
import { PopupContext } from './popup.context';

export type PopupProps = {
  children: React.ReactNode;
  /**
   * Controlled open state. Must be used together with `onOpenChange`.
   */
  isOpen?: boolean;
  /**
   * Uncontrolled initial open state, for one-shot auto-open.
   */
  isOpenByDefault?: boolean;
  /**
   * Called when open state changes. Required for controlled usage.
   */
  onOpenChange?: (open: boolean) => void;
};

/**
 * Presents more content in an expanded state after the user interacts with a trigger. Use `<Popup>` as a container around `<PopupButton>` and `<PopupContent>`.
 *
 * ---
 *
 * **Focus Lock**
 *
 * Per Radix docs, by default the ESC key can be used to close the popover.
 *
 * Due to Radix Popover locking focus in the popup content component when consuming, a11y also recommends an additional close button to be added if consuming this v2 component.
 * This is a known issue with Radix Popover and has yet to be addressed.
 *
 * **Recommended Solution**
 *
 * Use the Control Close component from [osc-controls](?path=/docs/components-controls--docs)
 */
export const Popup: React.FC<PopupProps> = ({
  isOpen,
  isOpenByDefault = false,
  onOpenChange,
  children,
}) => {
  const id = React.useId();
  const isControlled = isOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(isOpenByDefault);
  const popupIsOpen = isControlled ? isOpen : uncontrolledOpen;
  const triggerRef = React.useRef<HTMLButtonElement | HTMLInputElement>(null);
  const triggerClickedRef = React.useRef(false);

  const setOpen = (nextOpen: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }
    onOpenChange?.(nextOpen);
  };

  const closePopup = () => {
    setOpen(false);
  };

  return (
    <Popover.Root open={popupIsOpen} onOpenChange={setOpen}>
      <PopupContext.Provider
        value={{
          triggerRef,
          triggerClickedRef,
          popupId: id,
          isOpen: popupIsOpen,
          setOpen,
          closePopup,
        }}
      >
        {children}
      </PopupContext.Provider>
    </Popover.Root>
  );
};

export default Popup;
